// Angular
import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, TransferState, inject, makeStateKey } from '@angular/core';
// RxJS
import { Observable, from, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
// Constants
import { environment } from '@environment';
// Transloco
import { TranslocoService } from '@jsverse/transloco';
// Token
import { DOCUMENT_READER } from './document-reader.token';

@Injectable({
    providedIn: 'root'
})
export class DocumentsService {

    // Injections
    private readonly http = inject(HttpClient);
    private readonly translocoService = inject(TranslocoService);
    private readonly platformId = inject(PLATFORM_ID);
    private readonly transferState = inject(TransferState);

    /**
     * Server-only document reader
     * IMPORTANT:
     * This provider only exists during SSR
     * Browser bundle never imports:
     * - node:fs
     * - node:path
     */
    private readonly documentReader = inject(DOCUMENT_READER, { optional: true });

    /**
     * Get HTML document
     * 1. Browser checks TransferState
     * 2. If SSR response exists → reuse it
     * 3. SSR reads HTML directly from filesystem
     * 4. Browser fallback loads from /assets
     */
    public getDocument(page: string): Observable<any> {

        /**
         * Normalize page name
         * Example:
         * Bannerannonsering
         * -> bannerannonsering
         */
        const normalizedPage = page.trim().toLowerCase();

        /**
         * Generate readable title
         * Example:
         * privacy-policy
         * -> Privacy Policy
         */
        const title = normalizedPage.replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase());

        /** Current active language*/
        const lang = this.translocoService.getActiveLang();

        /**
         * Unique TransferState key
         * Example:
         * document_bildelsbasen_sv-se_privacy-policy
         */
        const stateKey = makeStateKey<string>(
            `document_${environment.orgId}_${lang}_${normalizedPage}`
        );

        /**
         * =====================================================
         * STEP 1:
         * Check TransferState cache
         * =====================================================
         * Browser receives SSR-rendered HTML and reuses it without another request
         */
        const cachedResponse = this.transferState.get(stateKey, null);
        if (cachedResponse) {
            this.transferState.remove(stateKey);
            return of({
                title,
                response: cachedResponse
            });
        }

        /**
         * =====================================================
         * STEP 2:
         * SERVER SIDE RENDERING (SSR)
         * =====================================================
         * Read HTML file directly from filesystem instead of making HTTP request
         */
        if (isPlatformServer(this.platformId) && this.documentReader) {
            return from(
                this.documentReader.read(lang, normalizedPage)
            ).pipe(
                tap((response) => this.transferState.set(stateKey, response)),
                map((response) => ({ title, response })),
                catchError(() => of(null))
            );
        }

        /**
         * =====================================================
         * STEP 3:
         * BROWSER FALLBACK
         * =====================================================
         * Browser loads HTML from assets folder
         */
        const documentURL = `/assets/documents/${environment.orgId}/${lang}/${normalizedPage}.html`;
        return this.http.get(documentURL, { responseType: 'text' }).pipe(
            map((response) => ({ title, response })),
            catchError(() => of(null))
        );
    }
}